import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

type AdminLoadingContextValue = {
  isLoading: boolean;
  track: <T>(task: () => Promise<T>) => Promise<T>;
};

const AdminLoadingContext = createContext<AdminLoadingContextValue | null>(null);

export const AdminLoadingProvider = ({ children }: { children: ReactNode }) => {
  const [activeRequestCount, setActiveRequestCount] = useState(0);

  const track = useCallback(async <T,>(task: () => Promise<T>) => {
    setActiveRequestCount((current) => current + 1);
    try {
      return await task();
    } finally {
      setActiveRequestCount((current) => Math.max(0, current - 1));
    }
  }, []);

  const value = useMemo<AdminLoadingContextValue>(() => ({
    isLoading: activeRequestCount > 0,
    track,
  }), [activeRequestCount, track]);

  return (
    <AdminLoadingContext.Provider value={value}>
      {children}
    </AdminLoadingContext.Provider>
  );
};

export const useAdminLoading = () => {
  const context = useContext(AdminLoadingContext);
  if (!context) {
    throw new Error("useAdminLoading must be used within AdminLoadingProvider");
  }
  return context;
};
